/**
 * The frame the « Les affinités » panel plays in, shared by both layouts.
 *
 * `GenerationStage` wraps the grid (desktop) or the day list (mobile): while
 * thinking it fades them to .32 — no scrim, the week is still visibly being
 * prepared behind — and centres the panel over them. `GenerationProgress` is
 * the 2px filament under the screen subtitle.
 *
 * Both follow `configPhase`. Leaving 'thinking' by any route (the cascade, a
 * cancel) drops the panel and restores full opacity, so nothing stays dimmed.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Platform, StyleSheet, View } from 'react-native';
import { useAppSelector } from '../../redux/hooks';
import {
  selectConfigPhase, selectConfigPlan, selectConfigScript, selectConfigShown,
} from '../../redux/mealPlanUISlice';
import { Paprika } from '../../styles/theme';
import {
  AFFINITY_FIRST_MS, AFFINITY_STEP_MS, AFFINITY_METER_DELAY_MS, AFFINITY_PANEL_OUT_MS,
} from '../weekly/v2/constants';
import { GenerationAffinityPanel } from './GenerationAffinityPanel';

const NATIVE = Platform.OS !== 'web';
const DIMMED = 0.32;
const DIM_MS = 450;

interface StageProps {
  compact?: boolean;
  /** Space a fixed bar covers at the bottom, so the panel centres on what is visible. */
  bottomInset?: number;
  children: React.ReactNode;
}

export function GenerationStage({ compact = false, bottomInset = 0, children }: StageProps) {
  const phase = useAppSelector(selectConfigPhase);
  const script = useAppSelector(selectConfigScript);
  const thinking = phase === 'thinking';
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (!thinking) {
      opacity.stopAnimation();
      opacity.setValue(1);
      return;
    }
    Animated.timing(opacity, { toValue: DIMMED, duration: DIM_MS, useNativeDriver: NATIVE }).start();
    // The week comes back as the panel steps aside, not after it has gone.
    const id = setTimeout(() => {
      Animated.timing(opacity, { toValue: 1, duration: DIM_MS, useNativeDriver: NATIVE }).start();
    }, AFFINITY_PANEL_OUT_MS);
    return () => clearTimeout(id);
  }, [thinking, opacity]);

  return (
    <View style={styles.stage}>
      <Animated.View
        style={[styles.fill, { opacity }]}
        testID="generation-stage"
        accessibilityElementsHidden={thinking}
        importantForAccessibility={thinking ? 'no-hide-descendants' : 'auto'}
      >
        {children}
      </Animated.View>
      {thinking && script && (
        <View
          style={[styles.overlay, compact && styles.overlayCompact, { bottom: bottomInset }]}
          pointerEvents="box-none"
        >
          <GenerationAffinityPanel script={script} compact={compact} />
        </View>
      )}
    </View>
  );
}

/**
 * 1/5, 2/5, 3/5 as the three candidates are weighed, then the rest as the
 * cascade reveals each cell. Hidden outside a generation.
 */
export function GenerationProgress({ width }: { width?: number | `${number}%` }) {
  const phase = useAppSelector(selectConfigPhase);
  const plan = useAppSelector(selectConfigPlan);
  const shown = useAppSelector(selectConfigShown);
  const [step, setStep] = useState(0);
  const fill = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (phase !== 'thinking') return;
    setStep(0);
    const ids = [0, 1, 2].map((k) => setTimeout(
      () => setStep(k + 1),
      AFFINITY_FIRST_MS + k * AFFINITY_STEP_MS + AFFINITY_METER_DELAY_MS,
    ));
    return () => ids.forEach(clearTimeout);
  }, [phase]);

  const total = Object.keys(plan).length;
  const target = phase === 'thinking'
    ? step / 5
    : phase === 'cascade' && total > 0
      ? 0.6 + 0.4 * Math.min(1, shown.length / total)
      : 0;

  useEffect(() => {
    Animated.timing(fill, {
      toValue: target,
      duration: 420,
      easing: Easing.bezier(0.4, 0, 0.2, 1),
      useNativeDriver: false,
    }).start();
  }, [target, fill]);

  if (phase !== 'thinking' && phase !== 'cascade') return null;
  return (
    <View style={[styles.track, width != null && { width }]} testID="generation-progress">
      <Animated.View
        style={[styles.bar, { width: fill.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }) }]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  stage: {
    flex: 1,
  },
  fill: {
    flex: 1,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  overlayCompact: {
    paddingHorizontal: 16,
  },
  track: {
    height: 2,
    borderRadius: 2,
    backgroundColor: Paprika.bgSunk,
    overflow: 'hidden',
  },
  bar: {
    height: '100%',
    backgroundColor: Paprika.primary,
  },
});
